import { subject as an } from '@casl/ability';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import {
  Stack,
  Typography,
  Button,
  Tooltip,
  Box,
  Card,
  CardContent,
  CardActions,
  IconButton,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { currencyFormatter, dateFormatter } from 'core/utils/commonUtils';
import { currencyCol } from 'core/utils/muiGridUtils';
import useResponsive from 'hooks/useResponsive';
import { isNil, random } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useId, useState, type FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form-mui';
import StoreContext from 'stores/context/StoreContext';
import MarkupEditDialog from './MarkupEditDialog';
import type { GridColDef } from '@mui/x-data-grid';
import type { AbilitySubjectsStr } from 'core/permissions/defineAbilityFor';
import type { ProjectProduct, ProjectProductMarkup } from 'models/order/orderProject';

const MarkupsEdit: FC = observer(() => {
  const componentId = useId();
  const isSmallScreen = useResponsive('down', 'sm');
  const [markupDialogOpen, setMarkupDialogOpen] = useState(false);
  const [markupFields, setMarkupFields] = useState<ProjectProductMarkup[]>([]);
  const [editedMarkup, setEditedMarkup] = useState<{
    index: number;
    item: ProjectProductMarkup;
  }>();

  const formContext = useFormContext<ProjectProduct>();
  const { fields, prepend, remove, update } = useFieldArray<ProjectProduct>({
    control: formContext.control,
    name: 'markups',
  });

  // Хак для решения проблемы https://github.com/orgs/react-hook-form/discussions/8935
  useEffect(() => {
    setMarkupFields(formContext.getValues('markups'));
  }, [formContext, fields]);

  const { userStore } = useContext(StoreContext);

  const canEdit = (m: ProjectProductMarkup): boolean =>
    userStore.ability?.can(
      'update',
      an<AbilitySubjectsStr, ProjectProductMarkup>('ProjectProductMarkup', m)
    ) ?? false;

  // const formValue = formContext.getValues(formPath) as ProjectProductMarkup;

  const handleAddMarkupClick = (): void => {
    setEditedMarkup(undefined);
    setMarkupDialogOpen(true);
  };

  const handleEdit = (index: number, item: ProjectProductMarkup): void => {
    setEditedMarkup({ index, item });
    setMarkupDialogOpen(true);
  };

  const columns: Array<GridColDef<ProjectProductMarkup>> = [
    {
      field: 'dateCreate',
      headerName: 'Дата',
      valueGetter: (value) => dateFormatter.format(value),
      renderCell: (params) => (
        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
          <Box>{params.value}</Box>
          <Tooltip title={`Создал: ${params.row.user?.fullName ?? ''}`}>
            <InfoOutlinedIcon fontSize={'inherit'} />
          </Tooltip>
        </Stack>
      ),
      minWidth: 120,
    },
    {
      field: 'amountPerItem',
      headerName: 'Наценка на ед. тов.',
      ...currencyCol,
      flex: 0.7,
    },
    { field: 'remark', headerName: 'Примечание', flex: 1 },
    {
      field: 'actions',
      maxWidth: 80,
      type: 'actions',
      getActions: (params) => {
        const rowIndex = markupFields.findIndex((row) => row.id === params.id);

        if (canEdit(params.row)) {
          return [
            <GridActionsCellItem
              key={`${componentId}-${params.id}-edit`}
              icon={<EditIcon />}
              onClick={() => {
                handleEdit(rowIndex, params.row);
              }}
              label='Редактировать'
            />,
            <GridActionsCellItem
              key={`${componentId}-${params.id}-delete`}
              icon={<DeleteIcon />}
              onClick={() => {
                remove(rowIndex);
              }}
              label='Удалить'
            />,
          ];
        } else {
          return [];
        }
      },
    },
  ];

  const handleMarkupDialogSave = (markup: ProjectProductMarkup): void => {
    setMarkupDialogOpen(false);

    if (!isNil(editedMarkup)) {
      update(editedMarkup.index, markup);
    } else {
      markup.id = -random(0, 0x80000000, false);
      prepend(markup);
    }

    setEditedMarkup(undefined);
  };

  const handleMarkupDialogCancel = (): void => {
    setMarkupDialogOpen(false);
  };

  const MarkupsArrayErrorMessage: FC = () =>
    !isNil(formContext.formState.errors.markups) &&
    formContext.formState.errors.markups.message ? (
      <Typography color='error' variant='caption' mb={0.5}>
        {formContext.formState.errors.markups.message}
      </Typography>
    ) : (
      <></>
    );

  const mobileProductList = markupFields.length ? (
    <Stack gap={2}>
      {markupFields.map((markup, index) => (
        <Card variant='outlined' key={markup.id}>
          <CardContent>
            <Stack
              direction={'row'}
              alignItems={'baseline'}
              justifyContent={'space-between'}>
              <Typography variant='body1'>
                {currencyFormatter.format(markup.amountPerItem)}
              </Typography>
              <Typography variant='caption'>
                {dateFormatter.format(markup.dateCreate)}
              </Typography>
            </Stack>

            <Typography variant='body2' color='text.secondary' mt={1}>
              Создал: {markup.user?.fullName ?? ''}
            </Typography>

            {markup.remark.length && (
              <Typography variant='body2' color='text.secondary'>
                Примечание: {markup.remark}
              </Typography>
            )}
          </CardContent>
          {canEdit(markup) && (
            <CardActions sx={{ pt: 0 }}>
              <IconButton
                aria-label='Редактировать наценку'
                size='small'
                onClick={() => {
                  handleEdit(index, markup);
                }}>
                <ModeEditIcon fontSize='small' />
              </IconButton>
              <IconButton
                aria-label='Удалить наценку'
                size='small'
                color='warning'
                onClick={() => {
                  remove(index);
                }}>
                <DeleteIcon fontSize='small' />
              </IconButton>
            </CardActions>
          )}
        </Card>
      ))}
    </Stack>
  ) : (
    <></>
  );

  return (
    <>
      {markupFields.length ? (
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          mt={2}
          mb={1.5}
          gap={2}>
          <Typography variant='body1' fontWeight={'bold'}>
            Наценки
          </Typography>
          <Button
            type='button'
            size='small'
            variant='outlined'
            onClick={handleAddMarkupClick}>
            Добавить наценку
          </Button>
        </Stack>
      ) : (
        <Button
          variant='text'
          size='small'
          onClick={handleAddMarkupClick}
          sx={{ alignSelf: 'end', fontSize: (theme) => theme.typography.caption }}>
          Добавить наценку
        </Button>
      )}

      <MarkupsArrayErrorMessage />

      {markupFields.length > 0 &&
        (isSmallScreen ? (
          mobileProductList
        ) : (
          <DataGrid
            rows={markupFields ?? []}
            columns={columns}
            disableColumnMenu
            hideFooter
            disableRowSelectionOnClick
            autoHeight
          />
        ))}

      <MarkupEditDialog
        open={markupDialogOpen}
        markup={editedMarkup?.item}
        onCancel={handleMarkupDialogCancel}
        onSave={handleMarkupDialogSave}
      />
    </>
  );
});

export default MarkupsEdit;
